import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import DiagramRenderer from "@/components/csat/DiagramRenderer";
import {
  Clock, Timer, ArrowLeft, ArrowRight, CheckCircle, XCircle,
  Flag, Crown, TrendingUp, Target, Award, RotateCcw, ChevronRight,
  Zap, Calculator, BookOpen, Lock, History, Eye, BarChart3,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const fmtClock = (secs) => {
  if (secs < 0) secs = 0;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

// Formats cramped MCQ stems (numbered statements, data-sufficiency, inline DI data) into readable lines.
const formatQuestionText = (raw) => {
  if (!raw) return "";
  let t = String(raw).trim().replace(/^Question\s*:\s*/i, "");

  // Put each numbered statement ("1. ...", "2) ...") on its own line.
  t = t.replace(/\s+(\d{1,2})[.)]\s+(?=[A-Z("'])/g, (m, n) => `\n${n}. `);

  // Put lead-ins and the final directive/question on their own lines.
  t = t.replace(
    /\s+(Statements?\s*:|Statement\s+(?:1|2|3|I|II|III)\s*:|Conclusions?\s*:|Conclusion\s+(?:1|2|I|II)\s*:|Graph\s+\d+|Table\s+\d+|Which\s+(?:of\s+the\s+(?:following|statements?|above|these)|is\/are|are|one|among)[^\n]*|How\s+many\s+of\s+the\s+(?:above|following)[^\n]*|Select\s+the\s+correct[^\n]*|Choose\s+the\s+correct[^\n]*|Directions?\s*:)/gi,
    (m, g) => "\n\n" + g.trim()
  );

  // One "Label = value" data pair per line when comma-separated.
  t = t.replace(/,\s+(?=[^,=\n]{1,45}=)/g, "\n");
  return t.replace(/\n{3,}/g, "\n\n").replace(/^\n+/, "").trim();
};

// Question figure: pre-rendered geometry SVG, else structured diagram.
const QFigure = ({ q, reveal }) => {
  if (q?.diagram_svg) {
    return (
      <div className="my-3 rounded-lg border border-slate-200 bg-white p-2 overflow-x-auto"
        data-testid="si-q-diagram-svg" dangerouslySetInnerHTML={{ __html: q.diagram_svg }} />
    );
  }
  if (q?.diagram) return <DiagramRenderer diagram={q.diagram} reveal={reveal} />;
  return null;
};

// Track config (aptitude vs general studies). Both are 200 Q · 180 min · 26 weeks.
const TRACKS = {
  aptitude: {
    base: "/si-weekly",
    title: "Arithmetic & Reasoning",
    chips: [["Arithmetic", 70], ["Advanced Maths", 30], ["Logical Reasoning", 60], ["Data Interpretation", 40]],
  },
  gs: {
    base: "/si-gs-weekly",
    title: "General Studies",
    chips: [["Polity", 30], ["History", 30], ["Geography", 25], ["Economy", 25], ["Sci & Tech", 20],
      ["Environment", 15], ["Int'l Relations", 10], ["Society & Security", 10], ["Current Affairs", 35]],
  },
};

// ── client-side paper cache (retakes never hit the API) ──────────────────────
const paperCacheKey = (tk, week) => `clarias_si_paper_${tk}_w${week}`;
const readPaperCache = (tk, week) => {
  try { return JSON.parse(localStorage.getItem(paperCacheKey(tk, week)) || "null"); } catch { return null; }
};
const writePaperCache = (tk, week, data) => {
  try { localStorage.setItem(paperCacheKey(tk, week), JSON.stringify(data)); } catch { /* quota */ }
};

// Score a retake entirely on the client from the cached review (correct answers + solutions).
const computeLocalResult = (reviewSource, answers, week, paperLabel, perQTime, timeTaken) => {
  let correct = 0, wrong = 0, skipped = 0;
  const secAcc = {};
  const review = reviewSource.map((r) => {
    const bucket = r.section_label || "General";
    const sa = secAcc[bucket] || { correct: 0, wrong: 0, skipped: 0, total: 0 };
    sa.total += 1;
    const sel = answers[r.question_id];
    const isCorrect = sel != null && sel !== "" && sel === r.correct_answer;
    if (sel == null || sel === "") { skipped += 1; sa.skipped += 1; }
    else if (isCorrect) { correct += 1; sa.correct += 1; }
    else { wrong += 1; sa.wrong += 1; }
    secAcc[bucket] = sa;
    return { ...r, user_answer: sel ?? null, is_correct: isCorrect, time_spent_seconds: perQTime[r.question_id] || 0 };
  });
  const total = reviewSource.length;
  const attempted = correct + wrong;
  const score = correct;
  const sectionWise = Object.entries(secAcc).map(([bucket, sa]) => {
    const att = sa.correct + sa.wrong;
    return { section: bucket, label: bucket, correct: sa.correct, wrong: sa.wrong, skipped: sa.skipped,
      total: sa.total, accuracy: att ? Math.round((sa.correct / att) * 1000) / 10 : 0 };
  });
  const pct = total ? Math.round((score / total) * 1000) / 10 : 0;
  const acc = attempted ? Math.round((correct / attempted) * 1000) / 10 : 0;
  const diffBreak = { hard: 0, medium: 0, easy: 0 };
  reviewSource.forEach((r) => {
    let d = String(r.difficulty || "medium").toLowerCase();
    if (!(d in diffBreak)) d = (d === "advanced" || d === "ultra_hard") ? "hard" : "medium";
    diffBreak[d] += 1;
  });
  diffBreak.total = total;
  diffBreak.hard_pct = total ? Math.round((diffBreak.hard / total) * 100) : 0;
  diffBreak.label = diffBreak.hard_pct >= 45 ? "Hard-heavy" : "Balanced";
  const summary = {
    paper: "retake", paper_label: paperLabel, week, total, attempted, correct, wrong, skipped,
    score, max_score: total, percentage: pct, accuracy: acc, time_taken_seconds: timeTaken,
    section_wise: sectionWise, difficulty_breakdown: diffBreak,
  };
  const label = acc >= 70 ? "Sharp & Consistent" : acc >= 50 ? "Steady" : acc >= 30 ? "Developing" : "Needs Work";
  const analysis = {
    behaviour_score: Math.round(acc),
    behaviour_label: label,
    verdict: `Self-practice retake: ${score}/${total} (${pct}%). Your leaderboard rank stays locked to your first attempt.`,
    is_retake: true,
  };
  return { summary, analysis, review };
};

const SITestSeries = ({ isPro, onBack }) => {
  const { toast } = useToast();
  const [phase, setPhase] = useState("hub"); // hub | weeks | intro | test | submitting | results
  const [track, setTrack] = useState("aptitude"); // aptitude | gs
  const [loading, setLoading] = useState(false);
  const [scheduleMap, setScheduleMap] = useState({}); // { aptitude: {...}, gs: {...} }
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingResult, setLoadingResult] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);

  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [result, setResult] = useState(null);
  const [reviewIdx, setReviewIdx] = useState(0);

  const schedule = scheduleMap[track] || null;
  const trackCfg = TRACKS[track];

  const startedAtRef = useRef(null);
  const timerRef = useRef(null);
  const qStartRef = useRef(Date.now());
  const perQTimeRef = useRef({});
  const submittingRef = useRef(false);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  const loadSchedule = useCallback(async (tk) => {
    try {
      const res = await axios.get(`${API_BASE}${TRACKS[tk].base}/schedule`, authHeader());
      setScheduleMap((prev) => ({ ...prev, [tk]: res.data }));
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    loadSchedule("aptitude");
    loadSchedule("gs");
  }, [loadSchedule]);

  const loadHistory = useCallback(async () => {
    try {
      const [apt, gs] = await Promise.all([
        axios.get(`${API_BASE}/si-weekly/history`, authHeader()).catch(() => ({ data: {} })),
        axios.get(`${API_BASE}/si-gs-weekly/history`, authHeader()).catch(() => ({ data: {} })),
      ]);
      const merged = [
        ...(apt.data.tests || []).map((t) => ({ ...t, _track: "aptitude" })),
        ...(gs.data.tests || []).map((t) => ({ ...t, _track: "gs" })),
      ].sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0));
      setHistory(merged);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { if (isPro) loadHistory(); }, [isPro, loadHistory]);

  const loadLeaderboard = useCallback(async (tk, week) => {
    try {
      const res = await axios.get(`${API_BASE}${TRACKS[tk].base}/leaderboard/${week}`, authHeader());
      setLeaderboard(res.data);
    } catch (e) { setLeaderboard(null); }
  }, []);

  const viewPastResult = async (tk, testId) => {
    setLoadingResult(true);
    try {
      const res = await axios.get(`${API_BASE}${TRACKS[tk].base}/attempt/${testId}`, authHeader());
      setTrack(tk);
      setResult(res.data); setReviewIdx(0);
      if (res.data.week) loadLeaderboard(tk, res.data.week);
      setPhase("results");
    } catch (e) {
      toast({ title: "Could not open result", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setLoadingResult(false); }
  };

  const openTrackWeeks = (tk) => { setTrack(tk); loadSchedule(tk); setPhase("weeks"); };

  const openIntro = (week) => {
    if (!week?.unlocked) return;
    setSelectedWeek(week);
    setPhase("intro");
  };

  const startTest = async () => {
    if (!selectedWeek) return;
    const week = selectedWeek.week;
    const paperLabel = `Week ${week} — ${trackCfg.title}`;

    // Retake path: if we cached this paper from the first attempt, replay it
    // entirely client-side (NO API call, leaderboard unchanged).
    const cached = readPaperCache(track, week);
    if (cached && cached.review && cached.review.length) {
      const questions = cached.review.map((r) => ({
        id: r.question_id, question: r.question, options: r.options,
        diagram: r.diagram, diagram_svg: r.diagram_svg, section_label: r.section_label,
      }));
      setTest({
        test_id: `local-${track}-${week}`,
        mode: "local",
        week,
        paper_label: paperLabel,
        num_questions: questions.length,
        duration_minutes: schedule?.duration_minutes || 180,
        questions,
        _reviewSource: cached.review,
      });
      setAnswers({}); setFlagged({}); setCurrentQ(0);
      setTimeLeft((schedule?.duration_minutes || 180) * 60);
      startedAtRef.current = Date.now();
      qStartRef.current = Date.now();
      perQTimeRef.current = {};
      setPhase("test");
      return;
    }

    // First attempt: hit the API (records score → locks the leaderboard).
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}${trackCfg.base}/start/${week}`, {}, authHeader());
      const d = res.data;
      const questions = (d.questions || []).map((q) => ({ ...q, section_label: q.bucket }));
      setTest({
        test_id: d.attempt_id,
        mode: "api",
        week: d.week,
        paper_label: paperLabel,
        num_questions: d.total_questions,
        duration_minutes: d.duration_minutes,
        questions,
      });
      setAnswers({}); setFlagged({}); setCurrentQ(0);
      const secs = d.deadline
        ? Math.max(0, Math.floor((new Date(d.deadline).getTime() - Date.now()) / 1000))
        : (d.duration_minutes || 180) * 60;
      setTimeLeft(secs);
      startedAtRef.current = Date.now();
      qStartRef.current = Date.now();
      perQTimeRef.current = {};
      setPhase("test");
    } catch (e) {
      toast({ title: "Unable to start", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const recordQTime = useCallback((qid) => {
    if (!qid) return;
    perQTimeRef.current[qid] = (perQTimeRef.current[qid] || 0) + Math.round((Date.now() - qStartRef.current) / 1000);
    qStartRef.current = Date.now();
  }, []);

  const goToQ = (idx) => { if (!test) return; recordQTime(test.questions[currentQ]?.id); setCurrentQ(idx); };

  const submitTest = useCallback(async (auto = false) => {
    if (submittingRef.current || !test) return;
    submittingRef.current = true;
    if (timerRef.current) clearInterval(timerRef.current);
    recordQTime(test.questions[currentQ]?.id);
    const timeTaken = Math.round((Date.now() - startedAtRef.current) / 1000);

    // Client-side retake: score locally, no API, leaderboard untouched.
    if (test.mode === "local") {
      const res = computeLocalResult(
        test._reviewSource, answers, test.week, test.paper_label, perQTimeRef.current, timeTaken
      );
      setResult(res); setReviewIdx(0);
      loadLeaderboard(track, test.week); // shows the locked (first-attempt) leaderboard
      setPhase("results");
      if (auto) toast({ title: "Time's up!", description: "Your retake was scored automatically." });
      submittingRef.current = false;
      return;
    }

    // First attempt: record on the server (locks the leaderboard), then cache
    // the full paper + solutions so every future retake stays client-side.
    setPhase("submitting");
    try {
      const res = await axios.post(
        `${API_BASE}${trackCfg.base}/submit/${test.test_id}`,
        { answers, time_taken_seconds: timeTaken, per_question_seconds: perQTimeRef.current },
        authHeader()
      );
      setResult(res.data); setReviewIdx(0); setPhase("results");
      writePaperCache(track, test.week, { review: res.data.review || [], cached_at: Date.now() });
      loadLeaderboard(track, test.week);
      loadHistory(); loadSchedule(track);
      if (auto) toast({ title: "Time's up!", description: "Your paper was submitted automatically." });
    } catch (e) {
      toast({ title: "Submission error", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
      setPhase("test"); submittingRef.current = false;
    }
  }, [test, answers, currentQ, track, trackCfg, recordQTime, toast, loadHistory, loadSchedule, loadLeaderboard]);

  useEffect(() => {
    if (phase !== "test") return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current); submitTest(true); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [phase, submitTest]);

  // ─────────────────────── HUB (paper picker / paywall) ───────────────────────
  if (phase === "hub") {
    if (!isPro) {
      return (
        <div className="min-h-screen bg-slate-50/40 py-10 px-4">
          <div className="max-w-2xl mx-auto">
            <Button variant="ghost" onClick={onBack} className="mb-4" data-testid="si-test-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Card className="border-2 border-amber-200 shadow-xl" data-testid="si-test-paywall">
              <CardContent className="p-8 text-center space-y-4">
                <div className="mx-auto w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center">
                  <Crown className="w-7 h-7 text-white" />
                </div>
                <Badge className="bg-amber-100 text-amber-800 border-0">CLARIAS PRO ONLY</Badge>
                <h2 className="text-2xl font-bold text-slate-900">SI Mains Test Series</h2>
                <p className="text-slate-600">
                  Simulate the full <b>200-question, 180-minute</b> SI Mains papers with a CLARIAS
                  behaviour-analytics report and full solution review.
                </p>
                <Button className="bg-gradient-to-r from-amber-500 to-orange-500 text-white" onClick={() => (window.location.href = "/upgrade")} data-testid="si-test-upgrade-btn">
                  Upgrade to PRO <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-slate-50/40 py-10 px-4">
        <div className="max-w-3xl mx-auto space-y-6">
          <Button variant="ghost" onClick={onBack} data-testid="si-test-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="text-center space-y-2">
            <div className="mx-auto w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center">
              <Timer className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">SI Mains Test Series</h1>
            <p className="text-slate-600">26-week timed series • 200 questions • 180 minutes • No negative marking</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4" data-testid="si-test-hub">
            {/* General Studies — 26-week timed series */}
            <Card
              className="border-2 border-emerald-100 hover:border-emerald-300 cursor-pointer hover:shadow-md transition-all"
              onClick={() => openTrackWeeks("gs")}
              data-testid="si-paper-gs"
            >
              <CardContent className="p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500">
                    <BookOpen className="w-5 h-5 text-white" />
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700 border-0">200 Q · 180 min</Badge>
                </div>
                <h3 className="font-bold text-slate-900">General Studies</h3>
                <div className="flex flex-wrap gap-1.5">
                  {TRACKS.gs.chips.map(([lbl, c]) => (
                    <span key={lbl} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{lbl} {c}</span>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs text-slate-500">
                    {scheduleMap.gs ? `${scheduleMap.gs.completed_count || 0}/${scheduleMap.gs.total_weeks || 26} done · new every Thursday` : "26 weekly tests · new every Thursday"}
                  </span>
                  <div className="flex items-center text-sm text-emerald-600 font-medium">
                    View 26 tests <ChevronRight className="w-4 h-4 ml-0.5" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Arithmetic & Reasoning — 26-week timed series */}
            <Card
              className="border-2 border-indigo-100 hover:border-indigo-300 cursor-pointer hover:shadow-md transition-all"
              onClick={() => openTrackWeeks("aptitude")}
              data-testid="si-paper-weekly"
            >
              <CardContent className="p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-500">
                    <Calculator className="w-5 h-5 text-white" />
                  </div>
                  <Badge className="bg-indigo-100 text-indigo-700 border-0">200 Q · 180 min</Badge>
                </div>
                <h3 className="font-bold text-slate-900">Arithmetic &amp; Reasoning</h3>
                <div className="flex flex-wrap gap-1.5">
                  {TRACKS.aptitude.chips.map(([lbl, c]) => (
                    <span key={lbl} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{lbl} {c}</span>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-xs text-slate-500">
                    {scheduleMap.aptitude ? `${scheduleMap.aptitude.completed_count || 0}/${scheduleMap.aptitude.total_weeks || 26} done · new every Thursday` : "26 weekly tests · new every Thursday"}
                  </span>
                  <div className="flex items-center text-sm text-indigo-600 font-medium">
                    View 26 tests <ChevronRight className="w-4 h-4 ml-0.5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* My SI Tests — history + behaviour trend */}
          {history.length > 0 && (
            <div className="space-y-4" data-testid="si-test-history">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-slate-900">My SI Tests</h2>
                <Badge className="bg-slate-100 text-slate-600 border-0">{history.length}</Badge>
              </div>

              {/* Behaviour / score trend */}
              {history.length >= 2 && (
                <Card className="border-indigo-100">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <BarChart3 className="w-4 h-4 text-indigo-600" />
                      <p className="text-sm font-semibold text-slate-700">Score Trend (out of 200)</p>
                    </div>
                    <div className="h-40" data-testid="si-test-trend-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={[...history].reverse().map((t, i) => ({
                          name: `T${i + 1}`,
                          score: t.summary?.score ?? 0,
                          accuracy: t.summary?.accuracy ?? 0,
                        }))} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                          <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                          <YAxis domain={[0, 200]} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                          <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                          <Line type="monotone" dataKey="score" stroke="#4f46e5" strokeWidth={3} dot={{ r: 4, fill: "#4f46e5" }} activeDot={{ r: 6 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Past papers list */}
              <div className="space-y-2">
                {history.map((t) => {
                  const s = t.summary || {};
                  const dt = t.completed_at ? new Date(t.completed_at) : null;
                  return (
                    <Card
                      key={t.test_id}
                      className="border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer"
                      onClick={() => viewPastResult(t._track || "aptitude", t.test_id)}
                      data-testid={`si-history-item-${t.test_id}`}
                    >
                      <CardContent className="p-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shrink-0">
                            <Award className="w-5 h-5 text-white" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 text-sm truncate">{t.paper_label || "SI Mains Paper"}</p>
                            <p className="text-xs text-slate-400">
                              {dt ? dt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : ""}
                              {" · "}{s.correct ?? 0}/{s.total ?? 200} correct · {s.accuracy ?? 0}%
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {t.behaviour_label && (
                            <Badge className="bg-slate-100 text-slate-600 border-0 hidden sm:inline-flex">{t.behaviour_label}</Badge>
                          )}
                          <div className="text-right">
                            <p className="text-lg font-black text-indigo-700 leading-none">{s.score ?? 0}</p>
                            <p className="text-[10px] text-slate-400">/ {s.max_score ?? 200}</p>
                          </div>
                          <Eye className="w-4 h-4 text-slate-400" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  // ─────────────────────── WEEKS (26-week schedule) ───────────────────────
  if (phase === "weeks") {
    const weeks = schedule?.weeks || [];
    return (
      <div className="min-h-screen bg-slate-50/40 py-10 px-4">
        <div className="max-w-3xl mx-auto space-y-6">
          <Button variant="ghost" onClick={() => setPhase("hub")} data-testid="si-weeks-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="text-center space-y-2">
            <div className="mx-auto w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center">
              {track === "gs" ? <BookOpen className="w-7 h-7 text-white" /> : <Calculator className="w-7 h-7 text-white" />}
            </div>
            <h1 className="text-2xl font-bold text-slate-900">{trackCfg.title} · 26-Week Series</h1>
            <p className="text-slate-600">A new 200-Q · 180-min test unlocks every Thursday • No negative marking</p>
            {schedule && (
              <div className="flex justify-center gap-3 text-sm pt-1">
                <Badge className="bg-emerald-100 text-emerald-700 border-0">{schedule.completed_count || 0}/{schedule.total_weeks} done</Badge>
                <Badge className="bg-amber-100 text-amber-700 border-0"><Zap className="w-3 h-3 mr-1" />{schedule.streak || 0} streak</Badge>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="si-weeks-grid">
            {weeks.map((w) => {
              const done = w.status === "completed";
              return (
                <Card
                  key={w.week}
                  className={`border-2 transition-all ${done ? "border-emerald-200" : w.unlocked ? "border-indigo-100 hover:border-indigo-300 cursor-pointer hover:shadow-md" : "border-slate-200 opacity-70"}`}
                  onClick={() => openIntro(w)}
                  data-testid={`si-week-card-${w.week}`}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className={`flex-shrink-0 h-11 w-11 rounded-xl flex items-center justify-center font-bold ${done ? "bg-emerald-500 text-white" : w.unlocked ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-400"}`}>
                      {done ? <CheckCircle className="w-5 h-5" /> : w.unlocked ? w.week : <Lock className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">Week {w.week}</p>
                      <p className="text-xs text-slate-400">
                        {w.unlocked ? (w.best_score != null ? `Best: ${w.best_score}/200` : "Available now") : `Unlocks ${w.unlock_date}`}
                      </p>
                    </div>
                    {w.unlocked && (
                      <Badge className={done ? "bg-slate-100 text-slate-600 border-0" : "bg-indigo-100 text-indigo-700 border-0"}>
                        {done ? "Retake" : "Start"} <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (phase === "intro" && selectedWeek) {
    return (
      <div className="min-h-screen bg-slate-50/40 py-10 px-4">
        <div className="max-w-2xl mx-auto">
          <Button variant="ghost" onClick={() => setPhase("weeks")} className="mb-4" data-testid="si-intro-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to weeks
          </Button>
          <Card className="border-indigo-100 shadow-lg" data-testid="si-test-intro">
            <CardContent className="p-8 space-y-6">
              <div className="text-center space-y-2">
                <div className="mx-auto w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center">
                  <Timer className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900">Week {selectedWeek.week} — {trackCfg.title}</h1>
                <p className="text-slate-600">SI Mains simulation • This is rehearsal, not practice.</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: Target, label: `${schedule?.questions_per_test || 200} Questions` },
                  { icon: Clock, label: `${schedule?.duration_minutes || 180} Minutes` },
                  { icon: Award, label: "No Negative Marking" },
                ].map((it, i) => (
                  <div key={i} className="p-4 bg-white rounded-xl border border-slate-200 text-center">
                    <it.icon className="w-6 h-6 text-indigo-600 mx-auto mb-1" />
                    <p className="font-bold text-slate-900 text-xs">{it.label}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {trackCfg.chips.map(([lbl, c]) => (
                  <span key={lbl} className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">{lbl} · {c}</span>
                ))}
              </div>
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 text-sm text-amber-900 space-y-1">
                <p className="font-semibold">Before you begin:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>The timer auto-submits when {schedule?.duration_minutes || 180} minutes elapse.</li>
                  <li>No negative marking — attempt every question, never leave a blank.</li>
                  <li>You get a CLARIAS behaviour report + full solutions + leaderboard after submission.</li>
                </ul>
              </div>
              <Button className="w-full h-12 text-base bg-gradient-to-r from-blue-600 to-indigo-600 text-white" onClick={startTest} disabled={loading} data-testid="si-test-start-btn">
                {loading ? "Building your paper…" : `Start Week ${selectedWeek.week} Paper`}
                {!loading && <ArrowRight className="w-5 h-5 ml-2" />}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ─────────────────────── SUBMITTING ───────────────────────
  if (phase === "submitting") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4" />
          <p className="text-lg font-semibold text-slate-900">Scoring your paper…</p>
          <p className="text-sm text-slate-500">Generating your CLARIAS behaviour report.</p>
        </div>
      </div>
    );
  }

  // ─────────────────────── TEST (timed) ───────────────────────
  if (phase === "test" && test) {
    const q = test.questions[currentQ];
    const answeredCount = Object.keys(answers).length;
    const lowTime = timeLeft <= 300;
    return (
      <div className="min-h-screen bg-slate-50/40">
        <div className="sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge className="bg-indigo-100 text-indigo-800 border-0">{test.paper_label}</Badge>
              <span className="text-sm text-slate-500 hidden sm:inline">{answeredCount}/{test.num_questions} answered</span>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl font-mono font-bold text-lg ${lowTime ? "bg-red-100 text-red-700 animate-pulse" : "bg-slate-100 text-slate-800"}`} data-testid="si-test-timer">
              <Clock className="w-5 h-5" /> {fmtClock(timeLeft)}
            </div>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => submitTest(false)} data-testid="si-test-submit-btn">
              Submit Paper
            </Button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 py-6 grid lg:grid-cols-[1fr_240px] gap-6">
          <div className="space-y-4">
            <Card key={q.id} className="border-indigo-100 shadow-sm" data-testid="si-test-question-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <Badge className="bg-indigo-100 text-indigo-800">Q {currentQ + 1} / {test.num_questions}</Badge>
                  <button
                    onClick={() => setFlagged({ ...flagged, [q.id]: !flagged[q.id] })}
                    className={`flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg border ${flagged[q.id] ? "bg-amber-100 border-amber-300 text-amber-800" : "border-slate-200 text-slate-500"}`}
                    data-testid="si-test-flag-btn"
                  >
                    <Flag className="w-4 h-4" /> {flagged[q.id] ? "Marked" : "Mark for review"}
                  </button>
                </div>
                <div className="text-xs text-slate-500 mb-3">{q.section_label}</div>
                <QFigure q={q} reveal={false} />
                <p className="text-lg text-slate-900 mb-6 leading-relaxed whitespace-pre-wrap">{formatQuestionText(q.question)}</p>
                <div className="space-y-3">
                  {q.options?.map((opt) => {
                    const selected = answers[q.id] === opt.key;
                    return (
                      <button
                        key={opt.key}
                        onClick={() => setAnswers({ ...answers, [q.id]: opt.key })}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selected ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:border-indigo-300 hover:bg-slate-50"}`}
                        data-testid={`si-test-option-${opt.key}`}
                      >
                        <span className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${selected ? "bg-indigo-500 text-white" : "bg-slate-100 text-slate-600"}`}>{opt.key.toUpperCase()}</span>
                          <span className="text-slate-700">{opt.text}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center justify-between mt-6">
                  <Button variant="outline" disabled={currentQ === 0} onClick={() => goToQ(currentQ - 1)} data-testid="si-test-prev-btn">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Previous
                  </Button>
                  {answers[q.id] && (
                    <Button variant="ghost" className="text-slate-500" onClick={() => { const a = { ...answers }; delete a[q.id]; setAnswers(a); }}>Clear</Button>
                  )}
                  <Button disabled={currentQ === test.num_questions - 1} onClick={() => goToQ(currentQ + 1)} className="bg-indigo-600 text-white" data-testid="si-test-next-btn">
                    Next <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:sticky lg:top-20 h-fit">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs font-semibold text-slate-500 mb-3">QUESTION PALETTE</p>
                <div className="grid grid-cols-6 lg:grid-cols-5 gap-2 max-h-[420px] overflow-y-auto" data-testid="si-test-palette">
                  {test.questions.map((qq, idx) => {
                    const isAns = !!answers[qq.id];
                    const isFlag = !!flagged[qq.id];
                    return (
                      <button
                        key={qq.id}
                        onClick={() => goToQ(idx)}
                        className={`relative w-9 h-9 rounded-lg text-xs font-bold ${currentQ === idx ? "bg-indigo-600 text-white ring-2 ring-indigo-300" : isAns ? "bg-emerald-100 text-emerald-800" : "bg-white text-slate-500 border border-slate-200 hover:bg-slate-50"}`}
                        data-testid={`si-test-palette-${idx}`}
                      >
                        {idx + 1}
                        {isFlag && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-500 rounded-full" />}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-4 space-y-1.5 text-xs text-slate-500">
                  <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300" /> Answered</div>
                  <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-white border border-slate-300" /> Not answered</div>
                  <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-amber-500" /> Marked</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // ─────────────────────── RESULTS ───────────────────────
  if (phase === "results" && result) {
    const { summary, analysis, review } = result;
    const rv = review[reviewIdx] || {};
    const steps = (rv.solution_steps && rv.solution_steps.length) ? rv.solution_steps : (rv.explanation ? rv.explanation.split("\n").filter(Boolean) : []);
    return (
      <div className="min-h-screen bg-slate-50/40 py-8 px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          <Button variant="ghost" onClick={onBack} data-testid="si-test-results-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          <Card className="border-2 border-indigo-200 shadow-lg" data-testid="si-test-result-hero">
            <CardContent className="p-8 text-center space-y-3">
              <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center bg-indigo-100">
                <Award className="w-8 h-8 text-indigo-600" />
              </div>
              <p className="text-5xl font-black text-slate-900" data-testid="si-test-score">{summary.score}<span className="text-2xl text-slate-400"> / {summary.max_score}</span></p>
              <Badge className="bg-indigo-100 text-indigo-800 border-0">{summary.paper_label} • {summary.percentage}%</Badge>
              {result.rank && (
                <div className="flex justify-center">
                  <Badge className="bg-amber-100 text-amber-800 border-0" data-testid="si-test-rank">
                    <Award className="w-3.5 h-3.5 mr-1" /> Rank #{result.rank} / {result.participants}
                  </Badge>
                </div>
              )}
              <p className="text-slate-600 max-w-lg mx-auto">{analysis?.verdict}</p>
              <div className="grid grid-cols-4 gap-2 pt-3 max-w-md mx-auto text-sm">
                <div><p className="text-xl font-bold text-emerald-600">{summary.correct}</p><p className="text-xs text-slate-500">Correct</p></div>
                <div><p className="text-xl font-bold text-rose-600">{summary.wrong}</p><p className="text-xs text-slate-500">Wrong</p></div>
                <div><p className="text-xl font-bold text-slate-500">{summary.skipped}</p><p className="text-xs text-slate-500">Skipped</p></div>
                <div><p className="text-xl font-bold text-indigo-600">{summary.accuracy}%</p><p className="text-xs text-slate-500">Accuracy</p></div>
              </div>
              {summary.difficulty_breakdown && (
                <div className="flex flex-wrap items-center justify-center gap-2 pt-2" data-testid="si-test-difficulty-breakdown">
                  <Badge className={`border-0 ${summary.difficulty_breakdown.label === "Hard-heavy" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
                    {summary.difficulty_breakdown.label} · {summary.difficulty_breakdown.hard_pct}% hard
                  </Badge>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-100">Hard {summary.difficulty_breakdown.hard}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">Medium {summary.difficulty_breakdown.medium}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">Easy {summary.difficulty_breakdown.easy}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {leaderboard && leaderboard.top && leaderboard.top.length > 0 && (
            <Card className="border-indigo-100" data-testid="si-test-leaderboard">
              <CardContent className="p-6 space-y-3">
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-500" />
                  <h3 className="font-bold text-slate-900">Week {leaderboard.week} Leaderboard</h3>
                  <Badge className="bg-slate-100 text-slate-600 border-0">{leaderboard.participants} players</Badge>
                </div>
                <div className="space-y-1">
                  {leaderboard.top.slice(0, 10).map((r) => (
                    <div key={r.rank} className={`flex items-center justify-between py-1.5 px-2 rounded-lg text-sm ${r.is_you ? "bg-indigo-50 font-bold text-indigo-700" : "text-slate-600"}`} data-testid={`si-lb-row-${r.rank}`}>
                      <span>#{r.rank} {r.name}{r.is_you ? " (You)" : ""}</span>
                      <span>{r.score} · {fmtClock(r.time_taken_seconds || 0)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {analysis && (
            <Card className="border-indigo-100" data-testid="si-test-analysis">
              <CardContent className="p-6 space-y-5">
                <div className="flex items-center gap-2"><Zap className="w-5 h-5 text-indigo-600" /><h3 className="font-bold text-slate-900">CLARIAS Behaviour Report</h3></div>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="text-center px-5 py-3 bg-indigo-50 rounded-xl">
                    <p className="text-3xl font-black text-indigo-700">{analysis.behaviour_score}</p>
                    <p className="text-xs text-slate-500">Behaviour Score</p>
                  </div>
                  <Badge className="bg-slate-100 text-slate-700 border-0">{analysis.behaviour_label}</Badge>
                </div>
                {analysis.components && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(analysis.components).map(([k, v]) => (
                      <div key={k} className="p-3 bg-white border border-slate-200 rounded-xl">
                        <p className="text-lg font-bold text-slate-900">{v}</p>
                        <p className="text-xs text-slate-500 capitalize">{k.replace(/_/g, " ")}</p>
                      </div>
                    ))}
                  </div>
                )}
                {analysis.primary_failure_reason && (
                  <div className="p-4 bg-rose-50 rounded-xl border border-rose-200">
                    <p className="text-xs font-semibold text-rose-700 mb-1">WHERE YOU LOST MARKS</p>
                    <p className="text-sm text-slate-800">{analysis.primary_failure_reason}</p>
                  </div>
                )}
                {analysis.one_behaviour_to_fix && (
                  <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <p className="text-xs font-semibold text-amber-700 mb-1">ONE BEHAVIOUR TO FIX</p>
                    <p className="text-sm text-slate-800">{analysis.one_behaviour_to_fix}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-2">SECTION-WISE ACCURACY</p>
                  <div className="space-y-2">
                    {summary.section_wise?.map((s) => (
                      <div key={s.section} className="flex items-center gap-3">
                        <span className="text-sm text-slate-700 w-48 truncate">{s.label}</span>
                        <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${s.accuracy >= 60 ? "bg-emerald-500" : s.accuracy >= 40 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${s.accuracy}%` }} />
                        </div>
                        <span className="text-xs text-slate-500 w-24 text-right">{s.correct}/{s.total} • {s.accuracy}%</span>
                      </div>
                    ))}
                  </div>
                </div>
                {analysis.confidence_message && (
                  <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 flex items-start gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <p className="text-sm text-emerald-900">{analysis.confidence_message}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="border-slate-200" data-testid="si-test-review">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-slate-900">Solution Review</h3>
                <span className="text-sm text-slate-500">Q {reviewIdx + 1} / {review.length}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {review.map((r, idx) => (
                  <button
                    key={idx}
                    onClick={() => setReviewIdx(idx)}
                    className={`w-8 h-8 rounded-lg text-xs font-bold ${reviewIdx === idx ? "ring-2 ring-indigo-400 " : ""}${r.user_answer == null ? "bg-slate-100 text-slate-500" : r.is_correct ? "bg-green-200 text-green-800" : "bg-red-200 text-red-800"}`}
                    data-testid={`si-test-review-nav-${idx}`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
              <div key={rv.question_id} className="border-t pt-4">
                <div className="text-xs text-slate-500 mb-2">{rv.section_label}</div>
                <QFigure q={rv} reveal={true} />
                <p className="text-base text-slate-900 mb-4 leading-relaxed whitespace-pre-wrap">{formatQuestionText(rv.question)}</p>
                <div className="space-y-2">
                  {rv.options?.map((opt) => {
                    const isCorrect = opt.key === rv.correct_answer;
                    const isSel = opt.key === rv.user_answer;
                    return (
                      <div key={opt.key} className={`p-3 rounded-xl border-2 flex items-center justify-between ${isCorrect ? "border-green-500 bg-green-50" : isSel ? "border-red-500 bg-red-50" : "border-slate-200"}`}>
                        <span className="flex items-center gap-2">
                          <span className="font-bold text-slate-500 uppercase">{opt.key}.</span>
                          <span className="text-slate-700 text-sm">{opt.text}</span>
                        </span>
                        {isCorrect && <CheckCircle className="w-5 h-5 text-green-600" />}
                        {isSel && !isCorrect && <XCircle className="w-5 h-5 text-red-600" />}
                      </div>
                    );
                  })}
                </div>
                {(steps.length > 0 || rv.formula_used || rv.shortcut_method || rv.common_mistake) && (
                  <div className="mt-4 space-y-3" data-testid="si-test-explanation">
                    {steps.length > 0 && (
                      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <p className="text-xs font-semibold text-blue-700 mb-2">SOLUTION</p>
                        <ol className="list-decimal list-inside space-y-1 text-sm text-slate-800">
                          {steps.map((s, i) => (<li key={i} className="whitespace-pre-wrap">{String(s).replace(/^\d+[.)]\s*/, "")}</li>))}
                        </ol>
                      </div>
                    )}
                    {rv.formula_used && (
                      <div className="p-3 bg-indigo-50 rounded-xl border border-indigo-200">
                        <p className="text-xs font-semibold text-indigo-700 mb-1">FORMULA USED</p>
                        <p className="text-sm text-slate-800 font-mono whitespace-pre-wrap">{rv.formula_used}</p>
                      </div>
                    )}
                    {rv.shortcut_method && (
                      <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                        <p className="text-xs font-semibold text-emerald-700 mb-1">⚡ SMART APPROACH / SHORTCUT</p>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">{rv.shortcut_method}</p>
                      </div>
                    )}
                    {rv.common_mistake && (
                      <div className="p-3 bg-rose-50 rounded-xl border border-rose-200">
                        <p className="text-xs font-semibold text-rose-700 mb-1">COMMON MISTAKE</p>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">{rv.common_mistake}</p>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between mt-5">
                  <Button variant="outline" disabled={reviewIdx === 0} onClick={() => setReviewIdx(reviewIdx - 1)}><ArrowLeft className="w-4 h-4 mr-2" /> Prev</Button>
                  <Button disabled={reviewIdx === review.length - 1} onClick={() => setReviewIdx(reviewIdx + 1)} className="bg-indigo-600 text-white">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-center pb-6">
            <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white" onClick={() => { setResult(null); setLeaderboard(null); submittingRef.current = false; loadSchedule(track); setPhase("weeks"); }} data-testid="si-test-retake-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Back to weekly tests
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SITestSeries;
